import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card } from '../components/ui/card';
import { Shield, Save } from 'lucide-react';

const MODULES = ['companies', 'sites', 'users', 'suppliers', 'categories', 'products', 'machines', 'operations', 'imports', 'approvals', 'audit', 'analytics', 'reports', 'commercial'];
const ACTIONS = ['view', 'create', 'edit', 'delete'];

export default function PermissionsPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/users').then(r => setUsers(r.data)).catch(() => {}); }, []);

  const selectUser = (uid) => {
    const user = users.find(u => u.user_id === uid);
    if (user) {
      setSelectedUser(user);
      setPermissions(JSON.parse(JSON.stringify(user.permissions || {})));
    }
  };

  const togglePerm = (module, action) => {
    setPermissions(prev => ({
      ...prev,
      [module]: { ...(prev[module] || {}), [action]: !(prev[module]?.[action]) }
    }));
  };

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await api.put(`/users/${selectedUser.user_id}/permissions`, { permissions });
      toast.success('Permissions updated');
      const res = await api.get('/users'); setUsers(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="permissions-page">
      <div className="page-header">
        <div><h1 className="page-title">Permission Matrix</h1><p className="text-sm text-slate-500 mt-1">Configure granular access control per user</p></div>
        {selectedUser && <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="save-permissions-btn"><Save size={16} className="mr-1.5" />{saving ? 'Saving...' : 'Save Permissions'}</Button>}
      </div>

      <Card className="p-4 mb-6 bg-white border border-slate-200">
        <div className="flex items-center gap-4">
          <Shield size={20} className="text-slate-400" />
          <Select value={selectedUser?.user_id || ''} onValueChange={selectUser}>
            <SelectTrigger className="w-80 bg-slate-50" data-testid="select-user-permissions"><SelectValue placeholder="Select a user to configure permissions" /></SelectTrigger>
            <SelectContent>{users.filter(u => u.role !== 'super_admin').map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name} ({u.role?.replace(/_/g, ' ')}) - {u.email}</SelectItem>)}</SelectContent>
          </Select>
          {selectedUser && <span className="text-xs text-slate-500">Role: <span className="font-medium capitalize">{selectedUser.role?.replace(/_/g, ' ')}</span></span>}
        </div>
      </Card>

      {selectedUser ? (
        <div className="data-table-wrapper overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50">Module</TableHead>
                {ACTIONS.map(a => <TableHead key={a} className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-center min-w-[80px]">{a}</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {MODULES.map(m => (
                <TableRow key={m} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium text-slate-900 capitalize sticky left-0 bg-white">{m}</TableCell>
                  {ACTIONS.map(a => (
                    <TableCell key={a} className="text-center">
                      <Switch
                        checked={permissions[m]?.[a] || false}
                        onCheckedChange={() => togglePerm(m, a)}
                        data-testid={`perm-${m}-${a}`}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="text-center py-16">
          <Shield size={48} className="mx-auto text-slate-200 mb-4" />
          <p className="text-slate-400">Select a user to view and configure their permissions</p>
        </div>
      )}
    </div>
  );
}
